import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
    CultureService,
    EstensaBusWrapperService,
    EstensaIconService,
    EstensaWaitingService,
    RootState,
    RouterStackService,
    SeSideComponentType,
    TIMELINE_OPTIONS,
    ThemeService,
    seIsIntegrationAppConnected,
    seLogin,
    seOpenTooluploadDialog,
    sePdfModalToggle,
    seSignalrConnected,
    seToolbarSetTimeline,
    selectSeButtonsTimeline,
    selectSeButtonsToolbarRightSlider,
} from '@se/platform';
import { Logger } from '@se/se.platform.base';
import { Observable, combineLatest, delay, distinctUntilChanged, filter, map } from 'rxjs';
import { selectActiveSeSideComponentState } from '../../../../se-modules-platform/src/lib/se-side/redux/se-side-nav.selectors';

interface RightSliderState {
    isClosed: boolean;
    isVisible: boolean;
    overlap: boolean;
    overlapOpen: boolean;
}

@Component({
    selector: 'host-hybrid-root',
    templateUrl: './host-hybrid.component.html',
    styleUrls: ['./host-hybrid.component.scss'],
    standalone: false,
})
export class HostHybridComponent implements AfterViewInit, OnDestroy, OnInit {
    currentEstensaTheme: Observable<string>;
    isVNext: boolean = false;
    isNg2: boolean = false;
    isEmbedded = signal<boolean>(false);

    isWaiting$ = this.waitingService.isWaiting$.pipe(delay(0));

    rightSlider$: Observable<RightSliderState> = this.store.pipe(
        select(selectSeButtonsToolbarRightSlider),
        map((slider) => ({
            isClosed: slider.enabled && !slider.checked,
            isVisible: slider.enabled,
            overlap: slider.enabled && !slider.checked && slider.overlap,
            overlapOpen: slider.enabled && slider.checked && slider.overlap,
        })),
    );

    timelineOpen$ = this.store.pipe(select(selectSeButtonsTimeline));

    activeSideComponents$ = this.store.pipe(select(selectActiveSeSideComponentState));
    openvna: boolean = false;

    pdfModalConfig$ = this.store.pipe(select('pdfModal'));

    // constructor

    constructor(
        private cultureService: CultureService,
        private elementRef: ElementRef,
        private estensaBusWrapperService: EstensaBusWrapperService,
        private estensaIconService: EstensaIconService,
        private logger: Logger,
        private store: Store<RootState>,
        private router: Router,
        private routerStackService: RouterStackService,
        private themeService: ThemeService,
        private translateService: TranslateService,
        private waitingService: EstensaWaitingService,
        private zone: NgZone,
    ) {
        // DGS_AP - nuova azione o modificare stato per aggiornare 'se-external-component-container width'
    }

    // public functions
    ngOnDestroy() {
        this.isVNext = false;
        this.isNg2 = false;
    }

    ngOnInit() {
        this.setCulture();
        this.initTheme();
        this.initIcons();
        this.estensaBusWrapperService.busDataFired.subscribe((message) =>
            this.zone.run(() => this.handleBusMessage(message)),
        );

        this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
            if (e.url != null) {
                // Verifico se è una pagina Angular2 e\o una VNext
                this.isNg2 = e.url.startsWith('/new/') ? true : false;
                this.isVNext = e.url.startsWith('/new/vnext') ? true : false;
                this.isEmbedded.set(e.url.includes('/embedded') ? true : false);
                this.updateLayout();
                this.syncNg1();
            }
        });

        this.router.initialNavigation();
    }

    onPdfModalVisiblityChange(visible: boolean) {
        this.store.dispatch(sePdfModalToggle({ visible }));
    }

    ngAfterViewInit() {
        this.waitingService.isWaiting = false;

        combineLatest([
            this.timelineOpen$,
            this.rightSlider$,
            this.activeSideComponents$
        ])
            .pipe(
                map(([timeline, rightSlider, components]) => {
                    const hasHistory = Array.isArray(components) &&
                        components.some((c) => c.type === SeSideComponentType.patientHistory);

                    const shouldDisable = timeline.noShow || !rightSlider.isVisible || !hasHistory;

                    const nextStatus = shouldDisable
                        ? TIMELINE_OPTIONS.TIMELINE_DISABLED
                        : timeline.open
                            ? TIMELINE_OPTIONS.TIMELINE_OPEN
                            : TIMELINE_OPTIONS.TIMELINE_CLOSED;

                    return nextStatus;
                }),
                distinctUntilChanged()
            )
            .subscribe((nextStatus) => {
                this.store.dispatch(seToolbarSetTimeline({ status: nextStatus }));
            });
    }

    // private functions

    private updateLayout() {
        this.elementRef.nativeElement.setAttribute('style', this.isNg2 ? 'display:flex;' : 'display:none;');

        let ebitBodyApp = document.getElementById('ebitBodyApp');
        let footer = document.getElementById('footer');

        let header = document.getElementById('header'); // VNext
        let middleViewArea = document.getElementById('middleViewArea'); // VNext
        let headerText = document.getElementById('title-and-patient-row-container'); // VNext

        if (this.isNg2) {
            // Anugular2
            footer.classList.add('new-layout-display-none');

            if (this.isVNext) {
                // VNext
                ebitBodyApp.classList.add('vnext-layout-content');
                header.classList.add('new-layout-display-none');
                middleViewArea.classList.add('new-layout-middle-area');
                headerText.classList.add('new-layout-display-none');
            } else {
                ebitBodyApp.classList.add('new-layout-content');
            }
        } else {
            // Classic
            footer.classList.remove('new-layout-display-none');
            ebitBodyApp.classList.remove('vnext-layout-content');
            header.classList.remove('new-layout-display-none');
            middleViewArea.classList.remove('new-layout-middle-area');
            ebitBodyApp.classList.remove('new-layout-content');
            headerText.classList.remove('new-layout-display-none');
        }
    }

    private initIcons() {
        this.estensaIconService.initEstensaIcons();
    }

    private initTheme() {
        this.currentEstensaTheme = this.themeService.getTheme();
    }

    private handleBusMessage(message) {
        switch (message.messageType) {
            case 'IS_INTEGRATION_CONNECTION':
                this.store.dispatch(seIsIntegrationAppConnected({ isConnected: message.data }));
                break;
            case 'ON_LOGIN_MESSAGE':
                this.store.dispatch(seLogin({ sso: message.data.sso }));
                break;
            case 'ROUTER_NAVIGATION_REQUEST':
                this.routerStackService.go(message.data.url, message.data.params, undefined, { push: false });
                break;
            case 'SIGNALR_CONNECTION':
                this.store.dispatch(seSignalrConnected({ isConnected: message.data }));
                break;
            case 'ESTENSA_TOOL_VNA':
                this.zone.run(() => {
                    this.store.dispatch(seOpenTooluploadDialog(message));
                });
                break;
        }
    }

    private setCulture() {
        this.cultureService.initializeAsync().then(() => {
            const language = this.cultureService.getCulture();

            this.translateService.use(language);
            this.logger.debug('translateService uses ' + language);
        });
    }

    private syncNg1() {
        this.estensaBusWrapperService.notify({ messageType: 'NG2_APPLY_ASYNC' });
    }
}

export enum SeHtmlPageType {
    Unknown,
    Classic,
    Angular,
    VNext,
}
